import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Upload,
  FolderOpen,
  Trash,
  MagnifyingGlass,
  Image,
  VideoCamera,
  Waveform,
  File,
} from '@phosphor-icons/react'
import api from '../services/api'
import { toast } from 'sonner'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const FILE_TYPE_CONFIG = {
  image: { icon: Image, color: '#32ADE6', label: 'Imagen' },
  video: { icon: VideoCamera, color: '#D42B1E', label: 'Video' },
  audio: { icon: Waveform, color: '#FFD60A', label: 'Audio' },
  other: { icon: File, color: '#6E6E73', label: 'Archivo' },
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AssetCard({ asset, onDelete }) {
  const cfg = FILE_TYPE_CONFIG[asset.file_type] || FILE_TYPE_CONFIG.other
  const Icon = cfg.icon
  const fullUrl = asset.file_url?.startsWith('/api')
    ? `${BACKEND_URL}${asset.file_url}`
    : asset.file_url

  return (
    <div
      className="bg-ds-elevated border border-ds-border  overflow-hidden group hover:border-ds-border-mid transition-all duration-200"
      data-testid={`asset-card-${asset.id}`}
    >
      {/* Preview */}
      <div className="relative h-36 bg-ds-elevated flex items-center justify-center overflow-hidden">
        {asset.file_type === 'image' ? (
          <img
            src={fullUrl}
            alt={asset.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : asset.file_type === 'video' ? (
          <video src={fullUrl} className="w-full h-full object-cover" muted />
        ) : (
          <Icon size={40} style={{ color: cfg.color }} weight="duotone" />
        )}
        {/* Delete button */}
        <button
          onClick={() => onDelete(asset.id)}
          className="absolute top-2 right-2 p-1.5 bg-black/70  opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/30"
          data-testid={`delete-asset-${asset.id}`}
        >
          <Trash size={14} color="#D42B1E" />
        </button>
        {/* Type badge */}
        <div className="absolute bottom-2 left-2">
          <span
            className="text-[9px] font-bold px-2 py-0.5  bg-black/70"
            style={{ color: cfg.color }}
          >
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-white text-xs font-medium truncate" title={asset.name}>
          {asset.name}
        </p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-[#6E6E73] text-[10px]">{formatSize(asset.size || 0)}</p>
          {asset.tags?.length > 0 && (
            <div className="flex gap-1">
              {asset.tags.slice(0, 2).map(t => (
                <span
                  key={t}
                  className="text-[9px] text-[#6E6E73] bg-ds-elevated px-1.5 py-0.5 "
                >
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AssetsPage() {
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [uploading, setUploading] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [filterType, setFilterType] = useState('')
  const fileInputRef = useRef(null)

  const fetchAssets = useCallback(async () => {
    setLoading(true)
    try {
      const params = search ? { search } : {}
      const res = await api.get('/assets', { params })
      setAssets(res.data)
    } catch {
      toast.error('Error al cargar assets')
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    fetchAssets()
  }, [fetchAssets])

  const handleFileUpload = async e => {
    const files = Array.from(e.target.files)
    if (!files.length) return
    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('tags', tagInput)
        await api.post('/assets/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
      toast.success(`${files.length} archivo(s) subido(s)`)
      setTagInput('')
      fetchAssets()
    } catch (err) {
      toast.error('Error al subir archivo')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar este asset?')) return
    try {
      await api.delete(`/assets/${id}`)
      toast.success('Asset eliminado')
      fetchAssets()
    } catch {
      toast.error('Error al eliminar')
    }
  }

  const filteredAssets = filterType ? assets.filter(a => a.file_type === filterType) : assets

  const counts = assets.reduce((acc, a) => {
    acc[a.file_type] = (acc[a.file_type] || 0) + 1
    return acc
  }, {})

  return (
    <div className="p-6 lg:p-8 min-h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-2xl font-bold text-white tracking-tight"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Biblioteca de Assets
          </h1>
          <p className="text-[#6E6E73] text-sm mt-0.5">{assets.length} archivos guardados</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            placeholder="Tags (opcional)"
            className="bg-ds-elevated border border-ds-border  px-3 py-2 text-white text-sm placeholder-[#4A4A4A] focus:outline-none focus:ring-1 focus:ring-[#D42B1E] w-32"
            data-testid="asset-tags-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-[#D42B1E] hover:bg-ds-red-hover text-white px-4 py-2  text-sm font-semibold disabled:opacity-50 transition-colors"
            data-testid="upload-asset-btn"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent  animate-spin" />
            ) : (
              <Upload size={16} weight="bold" />
            )}
            {uploading ? 'Subiendo...' : 'Subir Archivo'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="file-input"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E73]"
          />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre o tag..."
            className="w-full bg-ds-elevated border border-ds-border  pl-9 pr-4 py-2.5 text-white text-sm placeholder-[#4A4A4A] focus:outline-none focus:ring-1 focus:ring-[#D42B1E]"
            data-testid="asset-search-input"
          />
        </div>
        <div className="flex gap-2">
          {[
            { value: '', label: 'Todos', count: assets.length },
            { value: 'image', label: 'Imágenes', count: counts.image || 0 },
            { value: 'video', label: 'Videos', count: counts.video || 0 },
            { value: 'audio', label: 'Audio', count: counts.audio || 0 },
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setFilterType(tab.value)}
              className={`px-3 py-2  text-xs font-medium transition-colors flex items-center gap-1.5 ${filterType === tab.value ? 'bg-[#D42B1E] text-white' : 'bg-ds-elevated border border-ds-border text-[#A1A1A6] hover:text-white'}`}
              data-testid={`filter-type-${tab.value || 'all'}`}
            >
              {tab.label}
              <span
                className={`text-[10px] ${filterType === tab.value ? 'text-white/70' : 'text-[#6E6E73]'}`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Assets Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="bg-ds-elevated  h-48 animate-pulse border border-ds-border"
            />
          ))}
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FolderOpen size={48} className="text-[#3A3A3A] mb-4" weight="duotone" />
          <h3
            className="text-white font-semibold text-lg mb-1"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {search || filterType ? 'Sin resultados' : 'Biblioteca vacía'}
          </h3>
          <p className="text-[#6E6E73] text-sm mb-4">
            {search || filterType
              ? 'Prueba con otro filtro'
              : 'Sube imágenes, videos y clips de tu producción'}
          </p>
          {!search && !filterType && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-[#D42B1E] text-white px-4 py-2  text-sm font-medium hover:bg-ds-red-hover transition-colors"
              data-testid="empty-upload-btn"
            >
              Subir primer archivo
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredAssets.map(asset => (
            <AssetCard key={asset.id} asset={asset} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  )
}
